/**
 * AnchoredHint — Floating-UI-positioned inline hint surface for short
 * theorem-code → name annotations and similar mode-pill semantics.
 *
 * The CSK demos already lean on Radix Tooltip for the bulk of their
 * anchored-hover affordances, and Radix routes its positioning through
 * `@floating-ui/react-dom`. AnchoredHint exists for the lighter-weight
 * inline cases (e.g. annotating a single mode label inside a paragraph)
 * where a full Radix Tooltip subtree is overkill — the component
 * consumes `@floating-ui/react`'s React hooks directly so consumers can
 * trigger the hint on hover or focus without taking a peer-dep on the
 * Floating UI library themselves.
 *
 * Token-driven: background, border, and text colour are all sourced
 * from the design system so a tokens.data.json recolour propagates here
 * without a code change.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";
import React, { useState } from "react";
import { GRAY } from "../../design-system/tokens.js";

export interface AnchoredHintProps {
  /** Element rendered as the anchor. */
  readonly children: React.ReactElement;
  /** Hint content rendered when the anchor receives hover or focus. */
  readonly content: React.ReactNode;
  /** Floating UI placement. Defaults to `top`. */
  readonly placement?: Placement;
  /** Hover-open delay, in ms. */
  readonly openDelay?: number;
  /** Hover-close delay, in ms. */
  readonly closeDelay?: number;
}

const HINT_STYLE: React.CSSProperties = {
  background: GRAY[900],
  border: `1px solid ${GRAY[700]}`,
  borderRadius: "0.375rem",
  color: "white",
  fontSize: "0.75rem",
  lineHeight: 1.4,
  maxWidth: "16rem",
  padding: "0.4rem 0.55rem",
  pointerEvents: "none",
  zIndex: 80,
};

/**
 * Render an inline anchored hint that opens on hover or focus and
 * closes on mouseout / blur / Escape.
 */
export const AnchoredHint = React.memo(function AnchoredHint({
  children,
  content,
  placement = "top",
  openDelay = 150,
  closeDelay = 100,
}: AnchoredHintProps): React.JSX.Element {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { open: openDelay, close: closeDelay } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  // Clone the anchor so we can attach refs and Floating-UI interaction
  // props without forcing the consumer to use a specific component.
  const anchor = React.cloneElement(
    children,
    getReferenceProps({
      ref: refs.setReference,
      ...(children.props as Record<string, unknown>),
    }),
  );

  return (
    <>
      {anchor}
      {open && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          data-csk-anchored-hint=""
          style={{ ...floatingStyles, ...HINT_STYLE }}
        >
          {content}
        </div>
      )}
    </>
  );
});
